'use client';

import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';

type Viewport = 'desktop' | 'mobile';

const ViewportContext = createContext<Viewport>('mobile');

export const DESKTOP_QUERY = '(min-width: 1024px)';

const VIEWPORT_COOKIE = 'shot_viewport';
// 14d — long enough to outlive a typical browsing session, short
// enough to refresh after a user permanently rotates a tablet or
// switches device profiles.
const VIEWPORT_COOKIE_MAX_AGE = 14 * 24 * 60 * 60;

function writeViewportCookie(value: Viewport): void {
  if (typeof document === 'undefined') return;
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${VIEWPORT_COOKIE}=${value}; Path=/; Max-Age=${VIEWPORT_COOKIE_MAX_AGE}; SameSite=Lax${secure}`;
}

interface ViewportProviderProps {
  initial: Viewport;
  children: ReactNode;
}

/**
 * Server-seeded viewport. `initial` comes from the `x-viewport-hint`
 * request header set by middleware (cookie first → UA fallback). On
 * the client a `matchMedia` listener (a) corrects the value if the
 * server guess and the actual viewport disagree, and (b) writes the
 * confirmed value into `shot_viewport` cookie so the next SSR
 * request gets a precise hint instead of a UA guess.
 *
 * First-ever visitor with no cookie: middleware UA-detects, server
 * renders best-effort, client confirms in <16ms, cookie set for next
 * navigation. Subsequent visits: cookie is authoritative.
 */
export function ViewportProvider({ initial, children }: ViewportProviderProps) {
  const [viewport, setViewport] = useState<Viewport>(initial);
  const lastWrittenRef = useRef<Viewport | null>(null);

  useEffect(() => {
    const mql = window.matchMedia(DESKTOP_QUERY);
    const apply = (matches: boolean) => {
      const next: Viewport = matches ? 'desktop' : 'mobile';
      setViewport(next);
      if (lastWrittenRef.current !== next) {
        writeViewportCookie(next);
        lastWrittenRef.current = next;
      }
    };
    apply(mql.matches);
    const handler = (e: MediaQueryListEvent) => apply(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  return <ViewportContext.Provider value={viewport}>{children}</ViewportContext.Provider>;
}

export function useViewport(): Viewport {
  return useContext(ViewportContext);
}

export function useIsDesktop(): boolean {
  return useContext(ViewportContext) === 'desktop';
}
